import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  inject
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

type LocationCategory = {
  initial: string;
  name: string;
  color: string;
  items: string[];
};

type MapTile = {
  id: string;
  left: number;
  top: number;
  url: string;
};

@Component({
  selector: 'app-location',
  standalone: true,
  templateUrl: './location.html',
  styleUrl: './location.css'
})
export class LocationComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapShell') mapShell?: ElementRef<HTMLDivElement>;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly tileSize = 256;
  private readonly minZoom = 14;
  private readonly maxZoom = 18;
  private readonly locationLat = -1.288760;
  private readonly locationLng = 36.789841;

  private resizeObserver: ResizeObserver | null = null;
  private dragPointerId: number | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragCenterPixelX = 0;
  private dragCenterPixelY = 0;

  readonly categories: LocationCategory[] = [
    {
      initial: 'R',
      name: 'Restaurants',
      color: '#8B4A2F',
      items: [
        'Ankole Grill Kilimani',
        'Hooyos Kitchen & Poetry',
        'Golis Restaurant and Cafe',
        'Moxies Lounge',
        'The Royale House Restaurant',
      ]
    },
    {
      initial: 'S',
      name: 'Shopping',
      color: '#3B6B5E',
      items: [
        'Yaya Centre',
        'The Junction Mall',
        'Westfield Mall',
        'Lavington Mall',
        'Artcaffé Market',
      ]
    },
    {
      initial: 'H',
      name: 'Hotels',
      color: '#2C4A6E',
      items: [
        'Royal Canaan Nairobi Hotel',
        'O\'Sinkirri',
        'Yaya Hotel',
        'Four Points by Sheraton',
        'Maa Hotel & Suites',
      ]
    },
    {
      initial: 'E',
      name: 'Education',
      color: '#6B4F1E',
      items: [
        'Anatolia International School',
        'International Leadership University',
        'Makini Schools',
        'Brookhurst International Schools – Lavington Campus',
        'Kiota School Kindergarten',
      ]
    },
    {
      initial: 'M',
      name: 'Medical Centers',
      color: '#4A6B5E',
      items: [
        'The Nairobi Hospital',
        'National Spinal Injury Referral Hospital',
        'Caremark Hospital',
        'Luton Hospital',
        'MP Shah Hospital',
      ]
    },
    {
      initial: 'L',
      name: 'Leisure',
      color: '#7A4E2D',
      items: [
        'Ngong Hills',
        'Nairobi National Museum',
        'Uhuru Gardens',
        'The Galleria Bowling & Entertainment',
        'Nairobi National Park',
      ]
    },
  ];

  viewportWidth = 760;
  viewportHeight = 520;
  zoom = 15;
  centerLat = this.locationLat;
  centerLng = this.locationLng;
  mapTiles: MapTile[] = [];
  markerLeft = 0;
  markerTop = 0;
  markerVisible = true;

  constructor() {
    this.refreshMap();
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId) || !this.mapShell?.nativeElement) {
      return;
    }

    requestAnimationFrame(() => {
      this.measureViewport();
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.measureViewport();
    });

    this.resizeObserver.observe(this.mapShell.nativeElement);
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  onPointerDown(event: PointerEvent) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const shell = event.currentTarget as HTMLElement | null;

    if (!shell) {
      return;
    }

    shell.setPointerCapture(event.pointerId);
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;

    const centerPixel = this.latLngToPixel(this.centerLat, this.centerLng, this.zoom);
    this.dragCenterPixelX = centerPixel.x;
    this.dragCenterPixelY = centerPixel.y;
  }

  onPointerMove(event: PointerEvent) {
    if (this.dragPointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - this.dragStartX;
    const deltaY = event.clientY - this.dragStartY;
    const newCenter = this.pixelToLatLng(
      this.dragCenterPixelX - deltaX,
      this.dragCenterPixelY - deltaY,
      this.zoom
    );

    this.centerLat = newCenter.lat;
    this.centerLng = newCenter.lng;
    this.refreshMap();
  }

  onPointerUp(event: PointerEvent) {
    if (this.dragPointerId !== event.pointerId) {
      return;
    }

    const shell = event.currentTarget as HTMLElement | null;
    shell?.releasePointerCapture(event.pointerId);
    this.dragPointerId = null;
  }

  onWheel(event: WheelEvent) {
    event.preventDefault();

    const shell = event.currentTarget as HTMLElement | null;

    if (!shell) {
      return;
    }

    const nextZoom = Math.max(
      this.minZoom,
      Math.min(this.maxZoom, this.zoom + (event.deltaY < 0 ? 1 : -1))
    );

    if (nextZoom === this.zoom) {
      return;
    }

    const rect = shell.getBoundingClientRect();
    this.applyZoom(nextZoom, event.clientX - rect.left, event.clientY - rect.top);
  }

  zoomIn() {
    this.applyZoom(this.zoom + 1, this.viewportWidth / 2, this.viewportHeight / 2);
  }

  zoomOut() {
    this.applyZoom(this.zoom - 1, this.viewportWidth / 2, this.viewportHeight / 2);
  }

  resetMap() {
    this.zoom = 15;
    this.centerLat = this.locationLat;
    this.centerLng = this.locationLng;
    this.refreshMap();
  }

  private applyZoom(nextZoom: number, anchorX: number, anchorY: number) {
    const clampedZoom = Math.max(this.minZoom, Math.min(this.maxZoom, nextZoom));

    if (clampedZoom === this.zoom) {
      return;
    }

    const centerPixel = this.latLngToPixel(this.centerLat, this.centerLng, this.zoom);
    const anchorGeo = this.pixelToLatLng(
      centerPixel.x + (anchorX - this.viewportWidth / 2),
      centerPixel.y + (anchorY - this.viewportHeight / 2),
      this.zoom
    );

    const anchorPixelNext = this.latLngToPixel(anchorGeo.lat, anchorGeo.lng, clampedZoom);
    const newCenter = this.pixelToLatLng(
      anchorPixelNext.x - (anchorX - this.viewportWidth / 2),
      anchorPixelNext.y - (anchorY - this.viewportHeight / 2),
      clampedZoom
    );

    this.zoom = clampedZoom;
    this.centerLat = newCenter.lat;
    this.centerLng = newCenter.lng;
    this.refreshMap();
  }

  private refreshMap() {
    const centerPixel = this.latLngToPixel(this.centerLat, this.centerLng, this.zoom);
    const firstPixelX = centerPixel.x - this.viewportWidth / 2;
    const firstPixelY = centerPixel.y - this.viewportHeight / 2;
    const firstTileX = Math.floor(firstPixelX / this.tileSize);
    const firstTileY = Math.floor(firstPixelY / this.tileSize);
    const offsetX = -(firstPixelX - firstTileX * this.tileSize);
    const offsetY = -(firstPixelY - firstTileY * this.tileSize);
    const cols = Math.ceil(this.viewportWidth / this.tileSize) + 3;
    const rows = Math.ceil(this.viewportHeight / this.tileSize) + 3;
    const tilesPerAxis = 2 ** this.zoom;
    const tiles: MapTile[] = [];

    for (let row = 0; row < rows; row += 1) {
      const tileY = firstTileY + row;

      if (tileY < 0 || tileY >= tilesPerAxis) {
        continue;
      }

      for (let col = 0; col < cols; col += 1) {
        const tileX = firstTileX + col;
        const wrappedX = ((tileX % tilesPerAxis) + tilesPerAxis) % tilesPerAxis;

        tiles.push({
          id: `${this.zoom}-${tileX}-${tileY}-${col}-${row}`,
          left: offsetX + col * this.tileSize,
          top: offsetY + row * this.tileSize,
          url: `https://basemaps.cartocdn.com/light_all/${this.zoom}/${wrappedX}/${tileY}.png`
        });
      }
    }

    this.mapTiles = tiles;

    const locationPixel = this.latLngToPixel(this.locationLat, this.locationLng, this.zoom);
    this.markerLeft = locationPixel.x - firstPixelX;
    this.markerTop = locationPixel.y - firstPixelY;
    this.markerVisible =
      this.markerLeft > -140 &&
      this.markerLeft < this.viewportWidth + 140 &&
      this.markerTop > -140 &&
      this.markerTop < this.viewportHeight + 140;
  }

  private measureViewport() {
    const shell = this.mapShell?.nativeElement;

    if (!shell) {
      return;
    }

    this.viewportWidth = Math.max(320, Math.round(shell.clientWidth));
    this.viewportHeight = Math.max(360, Math.round(shell.clientHeight));
    this.refreshMap();
  }

  private latLngToPixel(lat: number, lng: number, zoom: number) {
    const scale = this.tileSize * 2 ** zoom;
    const latRad = (lat * Math.PI) / 180;

    return {
      x: ((lng + 180) / 360) * scale,
      y: ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * scale
    };
  }

  private pixelToLatLng(x: number, y: number, zoom: number) {
    const scale = this.tileSize * 2 ** zoom;
    const lng = (x / scale) * 360 - 180;
    const mercator = Math.PI * (1 - (2 * y) / scale);
    const lat = (Math.atan(Math.sinh(mercator)) * 180) / Math.PI;

    return { lat, lng };
  }
}
